import React, { useCallback } from 'react';
import { ActivityIndicator, FlatList, StyleSheet, View } from 'react-native';

import { EmptyState, SkeletonRow, StatusChip, Text } from '~/components/core';
import AIStrategyBuilderService from '~/api-services/AIStrategyBuilderService';
import { StrategyChange } from '~/api-services/AIStrategyBuilderService/types';
import { FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { useStyles } from '~/hooks';
import { Theme } from '~/theme';

import { useStrategyPaged } from '../../hooks/useStrategyPaged';
import { formatStrategyDateTime } from '../../utils/format';

type Props = {
    strategyId: string;
};

export default function StatusChangesList({ strategyId }: Props) {
    const styles = useStyles(stylesGenerator);

    const fetchChanges = useCallback(
        (params: { page: number; size: number }) => AIStrategyBuilderService.getStrategyChanges(strategyId, params),
        [strategyId]
    );
    const { items, loading, loadingMore, error, refresh, loadMore } = useStrategyPaged<StrategyChange>(fetchChanges);

    if (loading && items.length === 0) {
        return (
            <View style={styles.skeletons}>
                <SkeletonRow height={20} width="100%" />
                <SkeletonRow height={20} width="100%" />
            </View>
        );
    }

    if (items.length === 0) {
        return error ? (
            <EmptyState title="ai-strategy-status-empty" body={error} ctaLabel="retry" onCtaPress={refresh} />
        ) : (
            <EmptyState title="ai-strategy-status-empty" body="ai-strategy-status-empty-body" />
        );
    }

    return (
        <FlatList
            style={styles.container}
            contentContainerStyle={styles.content}
            data={items}
            keyExtractor={(item, index) => `${item.created}-${item.status}-${index}`}
            initialNumToRender={20}
            onEndReachedThreshold={0.4}
            onEndReached={loadMore}
            renderItem={({ item }) => (
                <View style={styles.row}>
                    <StatusChip status={item.status} serverLabel={item.statusLabel} />
                    <Text noTranslate style={styles.time}>
                        {formatStrategyDateTime(item.created)}
                    </Text>
                </View>
            )}
            ListFooterComponent={
                loadingMore ? (
                    <View style={styles.footer}>
                        <ActivityIndicator />
                    </View>
                ) : null
            }
        />
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: { flex: 1 },
        content: { paddingHorizontal: scaleSize(12), paddingBottom: scaleSize(12) },
        skeletons: { padding: scaleSize(12) },
        row: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingVertical: scaleSize(10),
            borderBottomWidth: StyleSheet.hairlineWidth,
            borderBottomColor: theme.tabIconDefault,
        },
        time: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
            marginLeft: scaleSize(8),
        },
        footer: { paddingVertical: scaleSize(12) },
    });
